import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  InMemoryCache,
  Observable,
  split,
  type Reference,
  type StoreObject,
} from "@apollo/client";
import { ApolloProvider as BaseApolloProvider } from "@apollo/client/react";
import { createFragmentRegistry } from "@apollo/client/cache";
import { setContext } from "@apollo/client/link/context";
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import {
  getMainDefinition,
  relayStylePagination,
} from "@apollo/client/utilities";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { createClient } from "graphql-ws";
import { useEffect, useMemo } from "react";

import { logError } from "@/app/_/config/logError";
import { Fragments } from "@/constants";
import { Subscriptions, Values } from "@/constants";
import { Objects } from "@/objects";
import { State } from "@/state";
import { mergeMessageObjectContent } from "./mergeMessageObjectContent";
import type { FreshToken } from "./ThemeRegistry";
import { mergeThreadMessageEdges } from "@/components/organisms/Thread/Thread.utils";
import { ObjectsChangedSubscriber } from "../subscriptions/ObjectsChangedSubscriber";

import schema from "../../../../../../schema.graphql?raw";

const CONNECTION_PAYLOAD_TYPENAME_PATTERN = /^type (\w+ConnectionPayload) \{/gm;

/** Relay list connections expose ``data(first: …)``; exclude scalar payloads like RepairBankConnectionPayload. */
function connectionPayloadTypenamesFromSchema(schemaSource: string): string[] {
  const typenames: string[] = [];
  CONNECTION_PAYLOAD_TYPENAME_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while (
    (match = CONNECTION_PAYLOAD_TYPENAME_PATTERN.exec(schemaSource)) !== null
  ) {
    const typename = match[1];
    const blockStart = match.index + match[0].length;
    const block = schemaSource.slice(blockStart, blockStart + 800);
    if (block.includes("data(")) {
      typenames.push(typename);
    }
  }

  return typenames;
}

function buildConnectionPayloadTypePolicies() {
  return Object.fromEntries(
    connectionPayloadTypenamesFromSchema(schema).map((typename) => [
      typename,
      {
        // Merge writes into the existing payload instead of replacing it.
        //
        // These payload types carry no ``id``, so Apollo stores them inline on
        // the parent field rather than normalising them. Without ``merge`` an
        // incoming write swaps the whole object out — taking every sibling
        // ``data({...})`` key with it. That is why loading the Posted tab
        // silently destroyed the Bank feed list's cache entry, leaving its
        // read incomplete so ``cache-first`` fell through to the network on
        // every switch back. Switching *views* was unaffected: those are
        // different payload objects.
        merge: true,
        fields: {
          // Key on ``filter``/``order``, not just the connection's own args.
          // ``relayStylePagination()`` defaults to ``keyArgs: false``, which
          // stores every filter of a connection under one entry — so the
          // bank-feed, posted and unposted lists all shared a single ``data``
          // field and merged into each other. Writing any one of them (a
          // sidebar hover, a tab switch) repainted whichever list was on
          // screen. Naming the args gives each its own entry; args a
          // particular connection does not declare are simply absent from
          // its key.
          data: relayStylePagination(["filter", "order"]),
        },
      },
    ]),
  );
}

/**
 * Reports the errors an operation came back with (``logError``), and asks
 * for a reload when they say the tab's code is older than the schema.
 */
export function reportGraphqlErrors(
  errors: readonly { message: string; path?: readonly (string | number)[] }[],
  context: Record<string, string | number | boolean>,
  variables?: Record<string, unknown>,
): void {
  const variableLabels: Record<string, string> = {};
  if (variables) {
    for (const key of [
      "threadIdentifier",
      "messageIdentifier",
      "ruleIdentifier",
      "executionIdentifier",
      "identifier",
    ]) {
      const value = variables[key];
      if (typeof value === "string" && value) {
        variableLabels[`graphql.variable.${key}`] = value;
      }
    }
  }

  // A tab keeps the code it was opened with. Once a deploy has moved the schema
  // on, what that code asks for is refused before anything runs - a type or
  // field the backend no longer has, or a variable declared for one - and
  // nothing on the page can refresh.
  // Those refusals carry no ``path``; the person is asked to reload.
  if (
    errors.some(
      (error) =>
        !error.path &&
        [
          "Cannot query field",
          "Unknown type",
          "Unknown argument",
          "must have a selection of subfields",
          "must not have a selection",
          "is required, but it was not provided",
          "is not defined by type",
          // A variable its code declares for a field the deploy took away.
          "is never used in operation",
        ].some((refusal) => error.message.includes(refusal)),
    )
  ) {
    State.setState({ isClientOutdated: true });
  }

  errors.forEach((error, index) => {
    const path = error.path?.map(String).join(".") ?? "";
    logError(new Error(error.message), {
      source: "apollo.graphql_error",
      ...context,
      ...variableLabels,
      "graphql.error.kind": "validation",
      "graphql.error.index": index,
      ...(path ? { "graphql.error.path": path } : {}),
    });
  });
}

/**
 * What the server closes a socket nothing came on from the browser for five
 * minutes with (``SOCKET_IDLE_SECONDS``, the backend's ``IDLE_CLOSE_CODE``):
 * it is opened again on the person's next move, not at once.
 */
const IDLE_CLOSE_CODE = 4000;
/** How often, at most, the person being here is said: well inside five minutes. */
const ACTIVITY_PING_MS = 60_000;

let activeSocket: WebSocket | undefined;
let timedOut: ReturnType<typeof setTimeout> | undefined;
let lastPingedAt = 0;
let isClosedForIdleness = false;
/** Opens a socket closed for idleness again, while one waits for the person. */
let resumeAfterIdle: (() => void) | undefined;

/**
 * The person is here - they pressed, typed, scrolled, came back to the tab,
 * or are watching a reply stream into it. Said to the server by a ping, at
 * most once a minute, and the socket stays open; a socket it closed for
 * idleness meanwhile is opened again instead. A ping not answered within five
 * seconds is a dead socket, closed so it is opened again.
 */
function noteActivity() {
  if (document.visibilityState !== "visible") return;
  if (resumeAfterIdle) {
    const resume = resumeAfterIdle;
    resumeAfterIdle = undefined;
    resume();
    return;
  }
  if (
    activeSocket?.readyState !== WebSocket.OPEN ||
    Date.now() - lastPingedAt < ACTIVITY_PING_MS
  )
    return;
  lastPingedAt = Date.now();
  activeSocket.send(JSON.stringify({ type: "ping" }));
  clearTimeout(timedOut);
  timedOut = setTimeout(() => {
    if (activeSocket?.readyState === WebSocket.OPEN) {
      activeSocket.close(4408, "Request Timeout");
    }
  }, 5_000);
}

// Pretty-print every GraphQL operation (query / mutation / subscription)
// to the browser console. Dev-only — silently no-ops in production builds
// so we don't leak sensitive variables / responses. Uses collapsed groups
// so the timeline stays scannable; expand any one to inspect variables
// and the resolved data / errors.
const loggerLink = new ApolloLink((operation, forward) => {
  if (!import.meta.env.DEV) return forward(operation);

  const def = getMainDefinition(operation.query);
  const opType =
    def.kind === "OperationDefinition" ? def.operation : "fragment";
  const opName = operation.operationName || "(anonymous)";
  const start = performance.now();
  // Subscriptions emit many `next` values on one socket; label them as events so
  // the console is not read as "14 separate subscriptions".
  const tag =
    opType === "subscription"
      ? `GQL SUBSCRIPTION ${opName} · event`
      : `GQL ${opType.toUpperCase()} ${opName}`;
  const color =
    opType === "mutation"
      ? "color:#d97706"
      : opType === "subscription"
        ? "color:#7c3aed"
        : "color:#2563eb";

  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next: (result) => {
        const ms = (performance.now() - start).toFixed(1);

        console.groupCollapsed(`%c${tag}%c  ${ms}ms`, color, "color:inherit");

        console.log("variables:", operation.variables);
        if (result.errors?.length) {
          console.warn("errors:", result.errors);
        }

        console.log("data:", result.data);

        console.groupEnd();
        observer.next(result);
      },
      error: (err) => {
        const ms = (performance.now() - start).toFixed(1);

        console.groupCollapsed(
          `%c${tag} (network error)%c  ${ms}ms`,
          "color:#dc2626;font-weight:bold",
          "color:inherit",
        );

        console.log("variables:", operation.variables);

        console.error(err);

        console.groupEnd();
        observer.error(err);
      },
      complete: () => observer.complete(),
    });
    return () => sub.unsubscribe();
  });
});

/**
 * The client's cache: every type policy and merge the app relies on.
 *
 * Exported so tests run the same merges the app does - a streamed or
 * replayed thread turn only reaches the screen through these.
 */
export function createApolloCache(): InMemoryCache {
  /** Shallow-merge nested Query roots keyed by field args (e.g. StatementQuery). */
  const shallowMergeStoreObjects = (
    existing: Record<string, unknown> | undefined,
    incoming: Record<string, unknown>,
  ): Record<string, unknown> => ({
    ...(existing ?? {}),
    ...incoming,
  });

  /** Avoid wiping streamed message context/nodes when a sparse cache write omits them. */
  const preserveNonNormalizedList = (
    existing: unknown,
    incoming: unknown,
  ): unknown => {
    if (incoming == null) return existing;
    if (Array.isArray(incoming)) {
      return incoming.length === 0 && existing != null ? existing : incoming;
    }
    if (typeof incoming === "object") {
      const keys = Object.keys(incoming as object);
      if (keys.length === 0 && existing != null) return existing;
    }
    return incoming;
  };

  const connectionPayloadTypePolicies = buildConnectionPayloadTypePolicies();

  return new InMemoryCache({
    fragments: createFragmentRegistry(...Fragments),
    typePolicies: {
      Query: {
        fields: {
          statements: {
            merge(existing, incoming) {
              if (incoming == null) return existing;
              if (existing == null) return incoming;
              return shallowMergeStoreObjects(
                existing as Record<string, unknown>,
                incoming as Record<string, unknown>,
              );
            },
          },
        },
      },
      StatementQuery: {
        merge: true,
      },
      MessageContentObject: {
        fields: {
          context: { merge: preserveNonNormalizedList },
          nodes: { merge: preserveNonNormalizedList },
          execution: {
            merge: (existing, incoming) =>
              incoming == null ? existing : incoming,
          },
          ruleNode: {
            merge: (existing, incoming) =>
              incoming == null ? existing : incoming,
          },
        },
      },
      ...connectionPayloadTypePolicies,
      ThreadObjectConnection: {
        fields: {
          edges: {
            merge(
              existing: readonly any[] = [],
              incoming: readonly any[],
              { readField },
            ) {
              const byId = new Map<string, any>();
              for (const edge of existing) {
                const id = readField("id", edge.node) as string | undefined;
                if (id) byId.set(id, edge);
              }
              for (const edge of incoming) {
                const id = readField("id", edge.node) as string | undefined;
                if (id) byId.set(id, edge);
              }
              const merged = Array.from(byId.values());
              merged.sort((a, b) => {
                const aStart =
                  (readField("createdAt", a.node) as string | null) ?? "";
                const bStart =
                  (readField("createdAt", b.node) as string | null) ?? "";
                if (aStart === bStart) return 0;
                return aStart < bStart ? 1 : -1;
              });
              return merged;
            },
          },
        },
      },
      MessageObject: {
        fields: {
          content: {
            merge(existing, incoming, { readField }) {
              return mergeMessageObjectContent(existing, incoming, readField);
            },
          },
        },
      },
      ThreadObject: {
        fields: {
          messages: {
            keyArgs: false,
            merge(existing, incoming, { readField }) {
              return mergeThreadMessageEdges(existing, incoming, readField);
            },
          },
        },
      },
      PendingChangeObject: {
        // Unnormalized (no id): one change read by two queries - the queue's
        // with its identifier alone, a draft's with the review it is in - is
        // one object, merged field by field; another change in its place
        // replaces it.
        merge(existing, incoming, { readField, mergeObjects }) {
          return existing &&
            readField("identifier", existing) ===
              readField("identifier", incoming)
            ? mergeObjects(existing, incoming)
            : incoming;
        },
      },
      PendingSessionObject: {
        // Unnormalized (no id): a `...Thread` push carries no `changes`, so merge
        // field-by-field instead of replacing the embedded object.
        merge: true,
        fields: {
          changes: {
            merge(
              existing: readonly unknown[] = [],
              incoming: readonly unknown[],
              { readField },
            ) {
              if (!incoming?.length)
                return existing?.length ? existing : incoming;
              const byId = new Map<string, unknown>();
              for (const entry of existing) {
                const id = readField<string>(
                  "identifier",
                  entry as Reference | StoreObject,
                );
                if (id) byId.set(id, entry);
              }
              for (const entry of incoming) {
                const id = readField<string>(
                  "identifier",
                  entry as Reference | StoreObject,
                );
                if (id) byId.set(id, entry);
              }
              return [...byId.values()];
            },
          },
        },
      },
      ExecutionObjectConnection: {
        fields: {
          edges: {
            merge(
              existing: readonly any[] = [],
              incoming: readonly any[],
              { readField },
            ) {
              const byId = new Map<string, any>();
              for (const edge of existing) {
                const id = readField("id", edge.node) as string | undefined;
                if (id) byId.set(id, edge);
              }
              for (const edge of incoming) {
                const id = readField("id", edge.node) as string | undefined;
                if (id) byId.set(id, edge);
              }
              const merged = Array.from(byId.values());
              merged.sort((a, b) => {
                const aStart =
                  (readField("createdAt", a.node) as string | null) ?? "";
                const bStart =
                  (readField("createdAt", b.node) as string | null) ?? "";
                if (aStart === bStart) return 0;
                return aStart < bStart ? 1 : -1;
              });
              return merged;
            },
          },
        },
      },
    },
    possibleTypes: {
      // ``possibleTypes`` MUST list the actual GraphQL typenames so
      // Apollo can match ``...on FooObject { ... }`` fragments
      // delivered over ``objectsChanged``. The JS map key in
      // ``Objects`` is sometimes a legacy label (e.g. ``ThreadObject``
      // pointing at the GraphQL type ``ThreadObject``); use each
      // entry's ``typename`` rather than the key to avoid silent
      // cache-write no-ops on subscription deliveries.
      // A suggestion's line is no object of its own, but word of one comes all
      // the same: the account it posts to, made since for the catalogue entry
      // it named.
      IObject: [
        ...Object.values(Objects).map((o) => o.typename),
        "RuleSuggestionLineObject",
      ],
      // What an invoice prints is asked for on these, alike by its business
      // and by its payer (``PrintedDocument``, ``PrintedLineItem``).
      IDocument: ["DocumentObject", "PublicDocumentObject"],
      ILineItem: ["TransactionObject", "PublicTransactionObject"],
      ITax: ["TaxObject", "PublicTaxObject"],
    },
  });
}

function makeClient(freshToken?: FreshToken): ApolloClient {
  // A chat's reply streaming in, which a person watching it is there for.
  const streamThreadOperation = getMainDefinition(Subscriptions.STREAM_THREAD)
    .name?.value;

  const wsLink = new GraphQLWsLink(
    createClient({
      url: Values.URLS["GRAPHQL_WS"],
      // No pings of its own, which kept every tab's socket open for as long as
      // the tab was: the person being here keeps it open (``noteActivity``).
      // Closed for idleness, it is opened again once they are back; closed any
      // other way, as graphql-ws would - two, four, eight seconds and so on,
      // give or take a little.
      retryWait: async (retries) => {
        if (isClosedForIdleness) {
          await new Promise<void>((resume) => {
            resumeAfterIdle = resume;
          });
          return;
        }
        await new Promise((resolve) =>
          setTimeout(
            resolve,
            2 ** retries * 1_000 + 300 + Math.floor(Math.random() * 2_700),
          ),
        );
      },
      on: {
        connected: (socket, _payload, wasRetry) => {
          activeSocket = socket as WebSocket;
          isClosedForIdleness = false;
          // Opened again, its subscriptions with it: what was pushed while it
          // was closed never came, so what is on screen is read again - into
          // the cache, as a push would have reached it. Not refetched: each
          // query refetched says it is loading, and every view on screen
          // turned back to its skeleton when the person came back to the tab.
          if (wasRetry) {
            for (const shown of client.getObservableQueries("active")) {
              void client
                .query({
                  query: shown.query,
                  variables: shown.variables,
                  fetchPolicy: "network-only",
                })
                .catch(() => undefined);
            }
          }
        },
        closed: (event) => {
          activeSocket = undefined;
          if (timedOut !== undefined) {
            clearTimeout(timedOut);
            timedOut = undefined;
          }
          isClosedForIdleness =
            (event as CloseEvent | undefined)?.code === IDLE_CLOSE_CODE;
        },
        pong: (received) => {
          if (received && timedOut !== undefined) {
            clearTimeout(timedOut);
            timedOut = undefined;
          }
        },
      },
      connectionParams: async () => {
        // Each (re)connect with a token that has time left: the socket
        // reconnecting after the tab slept would otherwise hand over a spent one.
        const jwtToken = freshToken
          ? await freshToken()
          : localStorage.getItem("jwtToken");
        // A client's books opened are read on the socket too: each of the
        // grants its businesses are shared through.
        const externalGrant =
          State.getState().externalGrantIdentifiers.join(",");
        return {
          ...(jwtToken != null ? { Authorization: `JWT ${jwtToken}` } : {}),
          ...(externalGrant ? { externalGrant } : {}),
        };
      },
    }),
  );

  // What each subscription brings: a chat's reply streaming in is the person
  // being here (``noteActivity``), and the errors it comes with are reported
  // as an HTTP operation's are.
  const wsResultsLink = new ApolloLink((operation, forward) => {
    const def = getMainDefinition(operation.query);
    const opType =
      def.kind === "OperationDefinition" ? def.operation : "fragment";
    const opName = operation.operationName || "(anonymous)";
    const labels = {
      "graphql.operation.type": opType,
      "graphql.operation.name": opName,
      "network.protocol.name": "graphql-ws",
    };

    return new Observable((observer) => {
      const sub = forward(operation).subscribe({
        next: (result) => {
          if (opName === streamThreadOperation) noteActivity();
          if (result.errors?.length) {
            reportGraphqlErrors(result.errors, labels, operation.variables);
          }
          observer.next(result);
        },
        error: (err) => {
          // A subscription the backend refuses outright comes back as an error,
          // not as a result carrying errors: reported as the errors it holds.
          if (CombinedGraphQLErrors.is(err)) {
            reportGraphqlErrors(err.errors, labels, operation.variables);
          }
          observer.error(err);
        },
        complete: () => observer.complete(),
      });
      return () => sub.unsubscribe();
    });
  });
  // The errors a query or mutation comes back with, reported.
  const httpResultsLink = new ApolloLink((operation, forward) => {
    const def = getMainDefinition(operation.query);
    const opType =
      def.kind === "OperationDefinition" ? def.operation : "fragment";
    const opName = operation.operationName || "(anonymous)";

    return new Observable((observer) => {
      const sub = forward(operation).subscribe({
        next: (result) => {
          if (result.errors?.length) {
            reportGraphqlErrors(
              result.errors,
              {
                "graphql.operation.type": opType,
                "graphql.operation.name": opName,
              },
              operation.variables,
            );
          }
          observer.next(result);
        },
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      });
      return () => sub.unsubscribe();
    });
  });

  const httpLink = new UploadHttpLink({
    uri: Values.URLS["GRAPHQL"],
  });
  const authLink = setContext(async (_, { headers }) => {
    const jwtToken = freshToken
      ? await freshToken()
      : localStorage.getItem("jwtToken");
    // Working in a client's books, every request names the grants they are
    // opened through - one a business of theirs - comma-separated: the server
    // reads those businesses alone.
    const externalGrant = State.getState().externalGrantIdentifiers.join(",");
    return {
      headers: {
        ...headers,
        ...(jwtToken != null && headers?.Authorization == null
          ? { Authorization: `JWT ${jwtToken}` }
          : {}),
        ...(externalGrant ? { "X-External-Grant": externalGrant } : {}),
      },
    };
  });
  // Answered "Authentication required" - a token that ran out while the tab
  // slept, or one Clerk revoked - a request is sent once more with a token
  // minted for it; none to be had, the person is signed out and sent to sign
  // in (``AuthSync``), and the answer stands.
  const authRetryLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let sub: { unsubscribe: () => void } | undefined;
        let isRetrying = false;
        let hasRetried = false;
        const send = () => {
          isRetrying = false;
          sub = forward(operation).subscribe({
            next: (result) => {
              if (
                freshToken &&
                !hasRetried &&
                result.errors?.some(
                  (error) => error.message === "Authentication required",
                )
              ) {
                hasRetried = true;
                isRetrying = true;
                void freshToken({ rejected: true }).then((token) => {
                  if (token) {
                    send();
                  } else {
                    observer.next(result);
                    observer.complete();
                  }
                });
                return;
              }
              observer.next(result);
            },
            error: (error) => observer.error(error),
            complete: () => {
              if (!isRetrying) observer.complete();
            },
          });
        };
        send();
        return () => sub?.unsubscribe();
      }),
  );
  const httpLinkWithAuth = ApolloLink.from([
    httpResultsLink,
    authRetryLink,
    authLink,
    httpLink,
  ]);
  // Route every operation over HTTP except subscriptions. Mutations
  // (including those with file uploads — ``UploadHttpLink`` handles
  // both shapes) are intrinsically request/response and have no
  // benefit from multiplexing onto the long-lived ``graphql-ws``
  // socket; pinning them to HTTP avoids head-of-line blocking when
  // the WS connection is degraded (slow subscribers, reconnect
  // storms, dangling sockets after HMR / page reloads), which was
  // making ``executeRule`` take seconds to round-trip and
  // delaying the cache.modify-driven row insertion in the
  // executions sidebar.
  //
  // The legacy split also unintentionally routed *queries* through
  // the WS link; pinning those to HTTP too keeps them on the
  // hardened request/response pipeline and stops them stalling
  // alongside the subscription stream.
  const wsLinkWithResults = ApolloLink.from([wsResultsLink, wsLink]);
  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLinkWithResults,
    httpLinkWithAuth,
  );

  const client = new ApolloClient({
    cache: createApolloCache(),
    // What comes back carries ``__typename`` on every node, and what goes out
    // is often the same thing edited - a report's definition, a widget's
    // question. An input type has no such field, so a round trip that went
    // through the cache used to be rejected at the door; this takes it off on
    // the way out, everywhere, rather than at each place that sends one.
    link: ApolloLink.from([
      loggerLink,
      new RemoveTypenameFromVariablesLink(),
      link,
    ]),
    devtools: {
      enabled: true,
    },
  });

  return client;
}

export function ApolloProvider({
  children,
  freshToken,
}: React.PropsWithChildren<{ freshToken?: FreshToken }>) {
  const client = useMemo(() => makeClient(freshToken), [freshToken]);

  // Pressing, typing, scrolling, a touch or coming back to the tab: the person
  // is here, and the socket is kept open, or opened again (``noteActivity``).
  useEffect(() => {
    const events = [
      "pointerdown",
      "keydown",
      "wheel",
      "touchstart",
      "focus",
      "visibilitychange",
    ];
    for (const event of events) {
      window.addEventListener(event, noteActivity, {
        capture: true,
        passive: true,
      });
    }
    return () => {
      for (const event of events) {
        window.removeEventListener(event, noteActivity, { capture: true });
      }
    };
  }, []);

  return (
    <BaseApolloProvider client={client}>
      <ObjectsChangedSubscriber />
      {children}
    </BaseApolloProvider>
  );
}
